/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAffectedIndices,
  getCurrentSelectedIndices,
} from '@opengeoweb/core';
import type { CoreAppStore } from '@opengeoweb/store';
import { layerActions, layerSelectors } from '@opengeoweb/store';

/**
 * Selects a single feature of a feature layer and puts it in edit mode, the same
 * way as clicking the feature on the map does.
 * @param layerId id of the feature layer
 * @returns callback to select a feature by its feature id
 */
export const useSelectDrawFeature = (
  layerId?: string,
): ((featureId: string) => void) => {
  const dispatch = useDispatch();

  const geoJSON = useSelector((store: CoreAppStore) =>
    layerSelectors.getFeatureLayerGeoJSON(store, layerId),
  );
  const selectedFeatureIds = useSelector((store: CoreAppStore) =>
    layerSelectors.getSelectedFeatureIds(store, layerId),
  );

  return React.useCallback(
    (featureId: string): void => {
      if (!layerId || !geoJSON?.features.length) {
        return;
      }

      const newIndices = getCurrentSelectedIndices({
        geojson: geoJSON,
        selectedFeatureIds: [featureId],
      });
      if (!newIndices.length) {
        return;
      }

      const currentIndices = getCurrentSelectedIndices({
        geojson: geoJSON,
        selectedFeatureIds,
      });

      dispatch(
        layerActions.setSelectedFeatures({
          layerId,
          selectedFeatureIds: [featureId],
        }),
      );

      getAffectedIndices(currentIndices, newIndices).forEach((index) => {
        const isNowSelected = newIndices.includes(index);
        dispatch(
          layerActions.updateFeatureProperties({
            layerId,
            properties: {
              highlightSelectedFeature: isNowSelected,
              isSelected: isNowSelected,
            },
            targetFeatureIndex: index,
          }),
        );
      });

      const isReadonly =
        !!geoJSON.features[newIndices[0]].properties?.isReadonly;
      if (!isReadonly) {
        dispatch(
          layerActions.toggleFeatureMode({
            layerId,
            isInEditMode: true,
            drawMode: '',
          }),
        );
      }
    },
    [dispatch, geoJSON, layerId, selectedFeatureIds],
  );
};
